/** Range-scale rock masses. The range mask still owns mountain placement;
 * this profile supplies connected shoulders, planar buttresses and gullies.
 * No mesh resolution, tile ID or renderer state enters the height function. */
import { hashSeed, rngFromString } from './rng.js';
import { planarField } from './landforms.js';
import { SimplexNoise3D } from './noise.js';
import type { PlanetDef } from './universe.js';

export class MountainLandforms {
  readonly enabled:boolean;
  private readonly seed:number;
  private readonly frame:number[];
  private readonly ridge:SimplexNoise3D;
  private readonly vertices=new Map<string,number>();
  constructor(def:PlanetDef){
    // Glaciers and volcanic cones own their specialist relief; preserve those
    // recently approved physical surfaces and their native catchments.
    this.enabled=def.terrain.amplitudeRel>=.001&&def.archetype!=='ice'&&def.archetype!=='volcanic';
    this.ridge=new SimplexNoise3D(hashSeed(def.seed)^0x2);
    this.seed=hashSeed(`${def.seed}:mountain-masses:v1`);
    const rng=rngFromString(`${def.seed}:mountain-frame`);
    let ax=rng()-.5,ay=rng()-.5,az=rng()-.5;
    const l=Math.sqrt(ax*ax+ay*ay+az*az)||1;ax/=l;ay/=l;az/=l;
    let bx=Math.abs(ay)<.9?-az:0,by=Math.abs(ay)<.9?0:az,bz=Math.abs(ay)<.9?ax:-ay;
    const b=Math.sqrt(bx*bx+by*by+bz*bz)||1;bx/=b;by/=b;bz/=b;
    this.frame=[ax,ay,az,bx,by,bz,ay*bz-az*by,az*bx-ax*bz,ax*by-ay*bx];
  }
  /** A fixed geological lattice in warped range space, independent of terrain
   * tessellation. Interpolating shared ridge samples produces real broad rock
   * planes, not facets that change when the renderer changes its triangle grid. */
  profile(x:number,y:number,z:number):number {
    const m=this.frame,cell=.085;
    const px=(x*m[0]+y*m[1]+z*m[2])/cell;
    const py=(x*m[3]+y*m[4]+z*m[5])/cell;
    const pz=(x*m[6]+y*m[7]+z*m[8])/cell;
    const ix=Math.floor(px),iy=Math.floor(py),iz=Math.floor(pz),a=px-ix,b=py-iy,c=pz-iz;
    const at=(i:number,j:number,k:number):number=>{
      const key=`${i},${j},${k}`,cached=this.vertices.get(key);if(cached!==undefined)return cached;
      const qx=i*cell,qy=j*cell,qz=k*cell;
      const rx=qx*m[0]+qy*m[3]+qz*m[6],ry=qx*m[1]+qy*m[4]+qz*m[7],rz=qx*m[2]+qy*m[5]+qz*m[8];
      const ridge=this.ridge.ridged(rx,ry,rz,3,2,.35)*1.27;
      const carrier=planarField(qx*.85,qy*1.7,qz*1.7,this.seed);
      const crest=Math.max(0,1-Math.abs(carrier)*(carrier<0?1.35:2.05));
      // Multiplication retains the existing low drainage corridors. Additive
      // mountains would fill those valleys and change their entire habitat.
      const value=Math.max(0,ridge*(.70+.30*crest));
      if(this.vertices.size>=16384)this.vertices.clear();
      this.vertices.set(key,value);return value;
    };
    const v0=at(ix,iy,iz),v3=at(ix+1,iy+1,iz+1);
    if(a>=b){
      if(b>=c)return v0*(1-a)+at(ix+1,iy,iz)*(a-b)+at(ix+1,iy+1,iz)*(b-c)+v3*c;
      if(a>=c)return v0*(1-a)+at(ix+1,iy,iz)*(a-c)+at(ix+1,iy,iz+1)*(c-b)+v3*b;
      return v0*(1-c)+at(ix,iy,iz+1)*(c-a)+at(ix+1,iy,iz+1)*(a-b)+v3*b;
    }
    if(a>=c)return v0*(1-b)+at(ix,iy+1,iz)*(b-a)+at(ix+1,iy+1,iz)*(a-c)+v3*c;
    if(b>=c)return v0*(1-b)+at(ix,iy+1,iz)*(b-c)+at(ix,iy+1,iz+1)*(c-a)+v3*a;
    return v0*(1-c)+at(ix,iy,iz+1)*(c-b)+at(ix,iy+1,iz+1)*(b-a)+v3*a;
  }
}
